import type { Location, NavigateFunction } from 'react-router-dom';
import type { QueryClient } from 'react-query';

import type { IConversation } from 'src/api/landlord-messages-api';
import {
    removeLeadFromCollection,
    removeLeadFromConversations,
    removeLeadFromConversationSearch,
} from 'src/components/live-updates-reaction/lead-cache-reconciliation';
import type { AddNotificationFunction } from 'src/notifications/context';
import { queryKeys } from 'src/services/api/constants';
import type { ILead } from 'src/services/api/leads/types';
import type { ISearchResponse } from 'src/services/api/messages/use-conversations-search';

type LeadDeletionRouteActions = {
    location: Location;
    navigate: NavigateFunction;
    addNotification: AddNotificationFunction;
};

type HandleLeadDeletedOptions = {
    queryClient: QueryClient;
    leadUuid: string;
    routeActions: LeadDeletionRouteActions;
    refetchCollections: () => void;
};

export const handleLeadDeleted = ({
    queryClient,
    leadUuid,
    routeActions,
    refetchCollections,
}: HandleLeadDeletedOptions) => {
    void queryClient.cancelQueries([queryKeys.leads, leadUuid]);
    queryClient.setQueryData<ILead[]>([queryKeys.leads], (cachedLeads) => (
        removeLeadFromCollection(cachedLeads, leadUuid) ?? []
    ));

    const conversationsKey = [queryKeys.conversations];
    const cachedConversations = queryClient.getQueryData<IConversation[]>(
        conversationsKey,
    );
    if (cachedConversations) {
        queryClient.setQueryData<IConversation[]>(
            conversationsKey,
            removeLeadFromConversations(cachedConversations, leadUuid),
        );
    }
    queryClient.getQueriesData<ISearchResponse>([queryKeys.conversationsSearch])
        .forEach(([queryKey, search]) => {
            if (search) {
                queryClient.setQueryData(
                    queryKey,
                    removeLeadFromConversationSearch(search, leadUuid),
                );
            }
        });

    queryClient.removeQueries([queryKeys.leads, leadUuid]);
    queryClient.removeQueries([queryKeys.leadFacts, leadUuid]);
    queryClient.removeQueries([queryKeys.leadStageHistory, leadUuid]);
    // Message payloads own their attachments, so removing messages evicts both.
    queryClient.removeQueries([queryKeys.messages, leadUuid]);
    queryClient.removeQueries([queryKeys.tourSlotSuggestion, leadUuid]);
    queryClient.removeQueries([queryKeys.factSuggestions, leadUuid]);
    queryClient.removeQueries(
        [queryKeys.conversations, 'lead', leadUuid],
        { exact: true },
    );
    refetchCollections();

    const { location, navigate, addNotification } = routeActions;
    const searchParams = new URLSearchParams(location.search);
    const isOpenMessageLead =
        location.pathname.startsWith('/landlord/inner/messages/from-lead') &&
        searchParams.get('lead_uuid') === leadUuid;
    const isOpenLeadsPageLead =
        location.pathname.startsWith('/landlord/inner/leads') &&
        searchParams.get('uuid') === leadUuid;

    if (isOpenMessageLead) {
        navigate(
            searchParams.get('back_path') || '/landlord/inner/root',
            { replace: true },
        );
        addNotification('This lead was deleted in another session.', 'info');
    } else if (isOpenLeadsPageLead) {
        searchParams.delete('uuid');
        const remainingSearch = searchParams.toString();
        navigate(
            `${location.pathname}${remainingSearch ? `?${remainingSearch}` : ''}`,
            { replace: true },
        );
        addNotification('This lead was deleted in another session.', 'info');
    }
};
